import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {
    LocalStorage,
    CookieStorage,
    MemoryStorage,
} from '@salesforce/commerce-sdk-react/auth/storage'
import {getParentOrigin, isOriginTrusted, onClient} from '@salesforce/commerce-sdk-react/utils'

const DNT_COOKIE_NAME = 'dw_dnt'
const DWSID_COOKIE_NAME = 'dwsid'
const DEFAULT_SLAS_REFRESH_TOKEN_REGISTERED_TTL = 90 * 24 * 60 * 60

const isParentTrusted = isOriginTrusted(getParentOrigin())

const DATA_MAP = {
    access_token: {
        storageType: 'local',
        key: 'access_token',
    },
    customer_id: {
        storageType: 'local',
        key: 'customer_id',
    },
    usid: {
        storageType: 'cookie',
        key: 'usid',
    },
    enc_user_id: {
        storageType: 'local',
        key: 'enc_user_id',
    },
    expires_in: {
        storageType: 'local',
        key: 'expires_in',
    },
    id_token: {
        storageType: 'local',
        key: 'id_token',
    },
    idp_access_token: {
        storageType: 'local',
        key: 'idp_access_token',
    },
    token_type: {
        storageType: 'local',
        key: 'token_type',
    },
    refresh_token_guest: {
        storageType: 'cookie',
        key: isParentTrusted ? 'cc-nx-g-iframe' : 'cc-nx-g',
        callback: (store) => {
            store.delete(isParentTrusted ? 'cc-nx-iframe' : 'cc-nx')
        },
    },
    refresh_token_registered: {
        storageType: 'cookie',
        key: isParentTrusted ? 'cc-nx-iframe' : 'cc-nx',
        callback: (store) => {
            store.delete(isParentTrusted ? 'cc-nx-g-iframe' : 'cc-nx-g')
        },
    },
    refresh_token_expires_in: {
        storageType: 'local',
        key: 'refresh_token_expires_in',
    },
    customer_type: {
        storageType: 'local',
        key: 'customer_type',
    },
    access_token_sfra: {
        storageType: 'cookie',
        key: 'cc-at',
    },
    [DNT_COOKIE_NAME]: {
        storageType: 'cookie',
        key: DNT_COOKIE_NAME,
    },
    dwsid: {
        storageType: 'cookie',
        key: DWSID_COOKIE_NAME,
    },
}

const useAuth = () => {
    const {site} = useMultiSite()

    const storageOptions = {keySuffix: site.id}
    const serverStorageOptions = {
        keySuffix: site.id,
        sharedContext: true,
    }
    const stores = onClient()
        ? {
              cookie: new CookieStorage(storageOptions),
              local: new LocalStorage(storageOptions),
              memory: new MemoryStorage(storageOptions),
          }
        : {
              // Always use MemoryStorage on the server.
              cookie: new MemoryStorage(serverStorageOptions),
              local: new MemoryStorage(serverStorageOptions),
              memory: new MemoryStorage(serverStorageOptions),
          }

    const set = (name, value, options) => {
        const {key, storageType} = DATA_MAP[name]
        const storage = stores[storageType]
        storage?.set(key, value, options)
        DATA_MAP[name].callback?.(storage)
    }

    const clearECOMSession = () => {
        const {key, storageType} = DATA_MAP[DWSID_COOKIE_NAME]
        const store = stores[storageType]
        store?.delete(key)
    }

    const handleTokenResponse = (res) => {
        set('access_token', res.access_token)
        set('customer_id', res.customer_id)
        set('enc_user_id', res.enc_user_id)
        set('expires_in', `${res.expires_in}`)
        set('id_token', res.id_token)
        set('idp_access_token', res.idp_access_token)
        set('token_type', res.token_type)
        set('usid', res.usid)
        set('customer_type', 'registered')

        const refreshTokenKey = 'refresh_token_registered'
        const refreshTokenTTLValue = DEFAULT_SLAS_REFRESH_TOKEN_REGISTERED_TTL
        const expiresDate = new Date(Date.now() + refreshTokenTTLValue * 1000)
        set('refresh_token_expires_in', refreshTokenTTLValue.toString())
        set(refreshTokenKey, res.refresh_token, {
            expires: expiresDate,
        })

        clearECOMSession()
    }

    return {
        handleTokenResponse,
    }
}

export default useAuth
